import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import FilterBar from './components/FilterBar';
import useSeo from '@/hooks/useSeo';
import { getCategoryBySlug, filterArticles, type SortKey, type RangeKey } from '@/utils/data';

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { category: categoryParam } = useParams<{ category?: string }>();
  const slug = categoryParam || 'hot-news';
  const category = getCategoryBySlug(slug);

  const [searchParams, setSearchParams] = useSearchParams();
  const sub = searchParams.get('sub') || 'all';
  const range = (searchParams.get('range') as RangeKey) || 'all';
  const sort = (searchParams.get('sort') as SortKey) || 'latest';
  const [visible, setVisible] = useState(PAGE_SIZE);

  const results = useMemo(
    () => (category ? filterArticles({ category: slug, sub, range, sort }) : []),
    [category, slug, sub, range, sort],
  );

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [slug, sub, range, sort]);

  useSeo({
    title: category ? `${category.name} - 51吃瓜网` : '分类 - 51吃瓜网',
    description: category
      ? `${category.name}栏目：${category.desc} 51吃瓜网实时更新${category.name}相关娱乐资讯与热门爆料。`
      : '51吃瓜网分类栏目，聚合热点新闻、爆料报道与网络趣闻。',
    keywords: category ? `${category.name},51吃瓜网,娱乐资讯,吃瓜资源` : '51吃瓜网,娱乐资讯',
  });

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all' || (value === 'latest' && key === 'sort')) next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  if (!category) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 py-24 text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-3xl" />
          </div>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-900">找不到这个分类</h1>
          <p className="mt-2 text-sm text-foreground-500">试试其他分类吧，看看今日有哪些新瓜。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const shown = results.slice(0, visible);

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <Breadcrumb items={[{ label: category.name }]} />

        <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8 mb-6">
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
              <i className={`${category.icon} text-2xl`} />
            </span>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{category.name}</h1>
              <p className="mt-1 text-sm text-foreground-500">{category.desc}</p>
            </div>
          </div>
        </div>

        <FilterBar
          category={category}
          sub={sub}
          range={range}
          sort={sort}
          onSubChange={(v) => updateParam('sub', v)}
          onRangeChange={(v) => updateParam('range', v)}
          onSortChange={(v) => updateParam('sort', v)}
        />

        <p className="mb-4 text-sm text-foreground-400">共找到 {results.length} 条资讯</p>

        {shown.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {shown.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>

            {visible < results.length ? (
              <div className="mt-8 text-center">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="inline-flex items-center gap-1.5 px-6 py-3 rounded-md border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  加载更多 <i className="ri-arrow-down-line" />
                </button>
              </div>
            ) : (
              <p className="mt-8 text-center text-sm text-foreground-400">已经到底啦，去看看其他分类吧</p>
            )}
          </>
        ) : (
          <div className="py-20 text-center rounded-lg bg-background-100 border border-background-200">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-50 text-foreground-400">
              <i className="ri-inbox-archive-line text-3xl" />
            </div>
            <p className="mt-5 font-heading text-lg font-semibold text-foreground-900">暂无相关资讯，试试其他分类吧！</p>
            <p className="mt-2 text-sm text-foreground-500">可以调整上面的筛选条件，或者回到首页看看热点。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line" /> 返回首页
            </Link>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}